/**
 * TASKS-EDIT.MODAL
 * Add or edit a task for an action plan
 */
import Modal from "@/components/modal"
import { WithTranslation, withTranslation } from "react-i18next"
import { connect } from "react-redux"
import { Session } from "@/redux/_session.types"
import {
  ActionPlanState,
  ActionPlanTask,
  ActionPlanTaskMessage,
  ActionPlanTaskStatus,
  TASK_STATUS_LIST
} from "@/redux/action-plan.types"
import { UserState } from "@/redux/user.types"
import { useState } from "react"
import { TopicState } from "@/redux/topic.types"
import { store } from "@/index"
import {
  actionPlanAddTask,
  actionPlanAddTaskMessage,
  actionPlanEditTask,
  actionPlanRemoveTask,
  actionPlanRemoveTaskMessage,
  actionPlanSendMessage
} from "@/redux/action-plan.actions"
import ModalConfirm from "@/components/modal-confirm"
import Space from "@/components/space"
import TasksTopicLabelWidget from "@/widgets/tasks-topic-label.widget"
import Link from "@/components/link"
import { toast } from "react-toastify"
import { formatDate } from "@/utils/format-date.utils"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagicWandSparkles, faPencilAlt, faTimes } from "@fortawesome/free-solid-svg-icons"
import Dropdown from "@/components/dropdown"
import TextInput from "@/components/text-input"
import { topicActivate } from "@/redux/topic.actions"
import TasksGenerateModal from "./tasks-generate.modal"
import DashboardTopicWidget from "@/widgets/dashboard-topic.widget"
import { Axis } from "@/redux/axis.types"
import Button from "@/components/button"

interface StateProps extends WithTranslation {
  _session: Session
  actionPlan: ActionPlanState
  topic: TopicState
  user: UserState
}

interface OwnProps {
  currentTask?: ActionPlanTask //Task to display
  isModeActive?: boolean //Edit mode or follow-up mode (with messages)
  onClose: Function //Trigger on close
}

type Props = StateProps & OwnProps

const MODAL_ASSISTANT = "MODAL_ASSISTANT"
const MODAL_DELETE = "MODAL_DELETE"
const MODAL_MESSAGE = "MODAL_MESSAGE"
const MODAL_MESSAGE_CONFIRM = "MODAL_MESSAGE_CONFIRM"

function TasksEditModal(props: Props) {
  const { t } = props

  //Get text
  const [currentTask, setCurrentTask] = useState(props.currentTask ? props.currentTask : new ActionPlanTask())

  //Current selected message (for delete)
  const [currentMessage, setCurrentMessage] = useState<null | ActionPlanTaskMessage>(null)

  //Display modal
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Active or not by default
  const [isEditable, setIsEditable] = useState(!props.isModeActive)

  //Write message to HR
  const [message, setMessage] = useState("")

  //Recipient of the email for messages
  const [recipient] = useState(props.user.active)

  //Delete a message
  function deleteMessage() {
    if (props.currentTask && currentMessage) {
      store.dispatch(actionPlanRemoveTaskMessage(props.currentTask.id, currentMessage.id))
    }

    setCurrentMessage(null)
  }

  //Delete the task
  function deleteTask() {
    if (props.currentTask) {
      store.dispatch(actionPlanRemoveTask(props.currentTask.id))
    }

    props.onClose()
  }

  //Update task
  function editCurrentTask(key: string, value: any) {
    setCurrentTask(Object.assign({}, currentTask, { [key]: value }))
  }

  //Change status
  function editStatus(status: string) {
    editCurrentTask("status", status)
    store.dispatch(actionPlanEditTask(Object.assign({}, currentTask, { status })))
  }

  //Custom title according status of the action plan
  function getTitle() {
    if (!isEditable) {
      return undefined
    } else if (props.currentTask) {
      return t("tasks_list_edit") + " "
    } else {
      return t("tasks_list_add")
    }
  }

  //Click on the wizard button
  async function handleAssistant() {
    if (currentTask.title === "") {
      setCurrentModal(MODAL_ASSISTANT)
    } else {
      alert("update")
    }
  }

  //Click on confirm
  async function next() {
    if (props.currentTask) {
      const updatedTask = new ActionPlanTask(currentTask)
      updatedTask.topicAid = props.topic.active.aid
      store.dispatch(actionPlanEditTask(updatedTask))
    } else {
      store.dispatch(
        actionPlanAddTask(props.topic.active.aid, currentTask.title, currentTask.text, props._session.userName)
      )
    }

    props.onClose()
  }

  //Send message to other contact
  //Save the message
  async function sendMessage() {
    if (props.currentTask) {
      store.dispatch(
        actionPlanAddTaskMessage(props.currentTask.id, message, props._session.userName, props._session.userId)
      )

      //Detect recipient
      //If admin it is the user responsible
      //If user it is the HR contact
      const isSendToAdmin = props._session.userRole !== "ADMIN" ? true : false

      if (recipient?.email) {
        const response: any = await store.dispatch(
          actionPlanSendMessage(
            props.actionPlan.active.id,
            props.currentTask.text,
            message,
            props._session.userName,
            isSendToAdmin,
            props.actionPlan.active.User.getInvitationLink(props._session)
          )
        )
        if (!response.error) {
          toast(t("utils_email_send", { email: recipient.email }))
        }
      }
    }

    setCurrentModal(null)
  }

  function StatusFrom() {
    return (
      <>
        {!currentTask.isDone && (
          <TextInput
            type="date"
            isFullWidth
            onChange={(e: any) => editCurrentTask("datePlanned", e.value ? new Date(e.value) : null)}
            title={t("tasks_date_planned")}
            value={currentTask.datePlanned ? formatDate(currentTask.datePlanned, false, true) : ""}
          />
        )}

        {props.actionPlan.active.status === "active" && (
          <Dropdown
            title={t("project_status")}
            value={t("tasks_list_status_" + currentTask.status)}
            displayField="name"
            active={currentTask.status}
            list={TASK_STATUS_LIST.map((x: ActionPlanTaskStatus) => {
              return {
                id: x,
                name: t("tasks_list_status_" + x)
              }
            })}
            onSelect={(status) => editStatus(status.id)}
          />
        )}
      </>
    )
  }

  return (
    <Modal
      isFullScreen
      isCloseButtonVisible
      disableClickOutside
      disableKeyEvents
      title={getTitle()}
      isLarge
      footer={
        isEditable &&
        props._session.isAssistantAllowed &&
        currentTask.text === "" &&
        currentTask.title === "" &&
        props.topic.active.aid && (
          <Button isWithBorder className="secondary" icon={faMagicWandSparkles} onClick={() => handleAssistant()}>
            {t("wizard_use")}
          </Button>
        )
      }
      onClose={() => props.onClose()}
      onDelete={props.currentTask ? () => setCurrentModal(MODAL_DELETE) : undefined}
      onNext={currentTask.text.length > 0 && isEditable ? next : undefined}>
      {currentModal === MODAL_DELETE && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={deleteTask}
          text={t("tasks_list_delete_ask")}
          textBold={t("utils_next_ask")}
        />
      )}

      {currentModal === MODAL_ASSISTANT && (
        <TasksGenerateModal selectedTopic={props.topic.active} onClose={() => props.onClose()} />
      )}

      {currentMessage && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          onYes={deleteMessage}
          text={t("message_ask_delete", {
            message: currentMessage.text
          })}
          textBold={t("utils_next_ask")}
        />
      )}

      {currentModal === MODAL_MESSAGE && (
        <Modal
          title={t("tasks_add_message")}
          onClose={() => setCurrentModal(null)}
          disableKeyEvents
          disableClickOutside
          isCloseButtonVisible
          onNext={() => {
            if (recipient?.email) {
              setCurrentModal(MODAL_MESSAGE_CONFIRM)
            } else {
              sendMessage()
            }
          }}>
          <p>{props.currentTask?.text}</p>

          <textarea
            style={{
              resize: "none",
              height: "140px",
              width: "calc(100% - 20px)",
              border: "none",
              padding: "6px 10px",
              backgroundColor: "#eaeaea",
              borderRadius: "8px",
              margin: "12px 0px"
            }}
            autoFocus
            onChange={(e) => setMessage(e.target.value)}
            value={message}></textarea>

          {recipient?.email && (
            <p className="grey-t">
              <b>
                {t(props._session.userRole === "ADMIN" ? "tasks_message_help_admin" : "tasks_message_help_user", {
                  name: recipient.username
                })}
              </b>
            </p>
          )}
        </Modal>
      )}

      {currentModal === MODAL_MESSAGE_CONFIRM && (
        <ModalConfirm
          onNo={() => setCurrentModal(null)}
          status={props.actionPlan.status}
          onYes={sendMessage}
          textBold={t("utils_next_ask")}
          text={t("tasks_launch_message", {
            name: recipient?.username,
            email: recipient?.email
          })}
        />
      )}

      {!isEditable ? (
        props.currentTask ? (
          <div className="flex">
            <div className="w-[360px]">
              {props.topic.active.aid && (
                <DashboardTopicWidget
                  isSmall
                  isOpen
                  currentTopic={props.topic.active}
                  currentAxis={props.topic.active.Axis ?? new Axis()}
                />
              )}
            </div>

            <div className="w-100" />

            <div className="flex-1">
              <TasksTopicLabelWidget currentTopic={props.currentTask.getTopic(props.topic.list)} />

              <div className="h-10" />

              <p style={{ fontSize: 18 }}>
                <b>{props.currentTask.title}</b>&nbsp;
                <FontAwesomeIcon icon={faPencilAlt} className="_hover" onClick={() => setIsEditable(true)} />
              </p>

              <p style={{ fontSize: 18 }}>{props.currentTask.text}</p>

              {props.currentTask.messages.map((message: ActionPlanTaskMessage) => (
                <div
                  key={message.id}
                  className="tasks-message grey-t"
                  style={
                    message.userId === props._session.userId
                      ? {
                          backgroundColor: props._session.accountColors.active,
                          color: "white",
                          marginLeft: 40
                        }
                      : {
                          backgroundColor: "#e6e6e6",
                          marginRight: 40
                        }
                  }>
                  <div className="flex">
                    {message.text}

                    <Space />

                    {message.userId === props._session.userId && props.actionPlan.active.status === "active" && (
                      <FontAwesomeIcon icon={faTimes} className="_hover" onClick={() => setCurrentMessage(message)} />
                    )}
                  </div>

                  <div className="flex tasks-message-footer">
                    <Space />
                    <b>{message.user}</b>
                  </div>

                  <div className="flex tasks-message-footer">
                    <Space />
                    {formatDate(message.date, true, false)}
                  </div>
                </div>
              ))}

              <div className="h-10" />

              {props.actionPlan.active.status === "active" && (
                <Link
                  onClick={() => {
                    setMessage("")
                    setCurrentModal(MODAL_MESSAGE)
                  }}
                  isWithoutMargin>
                  {t("tasks_add_message")}
                </Link>
              )}

              <div className="h-20" />

              {StatusFrom()}
            </div>
          </div>
        ) : null
      ) : (
        <div className="flex">
          <div className="w-[360px]">
            <div className="mb-20">
              <Dropdown
                displayField="label"
                value={props.topic.active.label}
                list={props.topic.list}
                title={t("topic")}
                onSelect={(e) => {
                  store.dispatch(topicActivate(e))
                  editCurrentTask("topicAid", e.aid)
                }}
              />
            </div>
            {props.topic.active.aid && (
              <DashboardTopicWidget
                isSmall
                isOpen
                currentTopic={props.topic.active}
                currentAxis={props.topic.active.Axis ?? new Axis()}
              />
            )}
          </div>
          <div className="w-100" />
          <div className="flex1">
            <TextInput
              autoFocus
              title={t("tasks_list_title")}
              value={currentTask.title}
              onChange={(e: any) => editCurrentTask("title", e.value)}
            />

            <div className="text-input-title flex grey-t">{t("tasks_list_text")}</div>

            <textarea
              className="text-area"
              rows={6}
              onChange={(e: any) => editCurrentTask("text", e.target.value)}
              value={currentTask.text}></textarea>

            {StatusFrom()}
          </div>
        </div>
      )}
    </Modal>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  actionPlan: state.actionPlan,
  topic: state.topic,
  user: state.user
})

export default connect(mapStateToProps)(withTranslation()(TasksEditModal))
